import { FC, useState } from "react";
import styled from "styled-components";
import { StockTextInputProps } from "../../utils/interfaces";
import { FieldLabel, NumberField, Readout } from "../fields";

/**
 * How many shares, and what that comes to.
 *
 * These were TWO INPUTS sharing one accent-blue box: the quantity on the
 * left, and on the right a second `InputNumber` marked readOnly holding the
 * indicative value. A control drawn as a field you type in, that then refuses
 * every keystroke — the same fault as the disabled read-out on the goal
 * pages. It is not an input; it is the answer to the one above it.
 *
 * So: one field, and one reading, both wearing the product's own field
 * chrome from `../fields` rather than a local copy of it.
 */
const Wrap = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: end;
`;

const Field = styled.div`
  min-width: 0;
`;

const money = (value: number) =>
  `$${(Number.isFinite(value) ? value : 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const StockTextInput: FC<StockTextInputProps> = ({
  stockPrice,
  leftLabel,
  rightLabel,
  onChange,
  maxQty,
  id,
}) => {
  const [amount, setAmount] = useState(1 * stockPrice);

  const handleChangeSharesNumber = (value: number) => {
    setAmount(value * stockPrice);
    onChange(value);
  };

  return (
    <Wrap>
      <Field>
        <FieldLabel htmlFor={id}>{leftLabel}</FieldLabel>
        <NumberField
          id={id}
          formatter={(value) => Math.round(Number(value)).toString()}
          parser={(value) => Math.round(+value)}
          defaultValue={1}
          min={1}
          max={maxQty}
          onChange={handleChangeSharesNumber}
        />
      </Field>
      <Field>
        {/* Not a label element: there is no control under it to focus. */}
        <FieldLabel as="span">{rightLabel}</FieldLabel>
        <Readout>{money(Number(amount))}</Readout>
      </Field>
    </Wrap>
  );
};
